import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import WorkflowContext from "../../context/WorkflowContext";
import styles from "./Metadata.module.scss";
import { MetadataModel } from "../../../../models/workflow.model";
import { FormikProps, getIn, useFormik } from "formik";
import * as Yup from "yup";
import SpinnerArea from "../../../../components/SpinnerArea";
import SaveSuccessDialog from "../../SaveSuccessDialog";
import CancelDialog from "../../CancelDialog";
import { useNavigate, useParams } from "react-router";
import useService from "../../../../hooks/useService";
import workflowService from "../../../../services/workflow.service";
import MainContext from "../../../../context/mainContext";

import SelectMultiple from "../../../../components/SelectMultiple";
import PlaceholderControl from "../../../../components/PlaceholderControl";
import { DocTypeModel } from "../../../../models/docType.model";
import globalDataService from "../../../../services/globalData.service";
import {
  PeoplePicker,
  PeoplePickerMultiple,
} from "../../../../components/SelectPicker/PeoplePickers";
import IconTooltip from "../../../../components/IconTooltip";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { ICategoryModel } from "../../../../models/category.model";
import { IRecordAccessClassificationModel } from "../../../../models/recordAccessClassification.model";
import { ContinueDialog } from "./MetadataDialogs";
import MetadataTranslation from "./MetadataTranslation";
import FileUploader, { FileWrapper } from "../../../../components/FileUploader";
import Covers from "./Covers";
import { LangCode } from "../../../../models/language.model";
import { validateBeforeExecute } from "../../../../misc/utils.functions";

const _emptyGeneralInfo: MetadataModel = {
  id: 0,
  title: "",
  author: null,
  countries: [],
  docType: {
    name: "",
    definition: "",
    examples: "",
    governance: "",
    levelOfApproval: "DepartmentChief",
    id: 0,
    mandatoryPeerReview: false,
    qualityReview: "",
  },
  languages: [],
  coAuthorInternals: [],
  leadingDepartments: [],
  otherDepartmentsInvolved: [],
  leadingDivisions: [],
  otherDivisionsInvolved: [],
  abstract: undefined,
  isCompleted: false,
  topics: [],
  canEdit: true,
  category: {
    id: 0,
    name: "",
  },
  creationDate: new Date(),
  recordAccessClassification: {
    id: 0,
    name: "",
  },
  finalVersionDocuments: [],
  translations: [],
  covers: [],
};

interface FormListData {
  languages: LangCode[];
  departments: string[];
  divisions: string[];
  docTypes: DocTypeModel[];
  countries: string[];
  topics: string[];
  categories: ICategoryModel[];
  accessClassifications: IRecordAccessClassificationModel[];
  sectors: string[];
}

const Metadata: React.FC = () => {
  const { ticketNumber } = useParams();
  const { service } = useContext(MainContext);
  const { currentWorkflow } = useContext(WorkflowContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const [selectedFinalDocument, setSelectedFinalDocument] = useState<string>();
  const [uploadCovers, setUploadCovers] = useState<FileWrapper[]>([]);

  const [cancelDialogShow, cancelDialogShowSet] = useState(false);
  const [continueDialogShow, continueDialogShowSet] = useState(false);

  const [creationDateError, setCreationDateError] = useState<string | null>();

  const [requiredDocumentError, setRequiredDocumentError] = useState<
    boolean | null
  >(null);

  const [model, loading, setModel] = useService(() =>
    workflowService.metadata(ticketNumber!)
  );
  const [formListData, loadingListData] = useService(() => {
    return Promise.all([
      globalDataService.languages(),
      globalDataService.departments(),
      globalDataService.divisions(),
      globalDataService.docTypes(),
      globalDataService.countries(),
      globalDataService.topics(),
      globalDataService.categories(),
      globalDataService.recordAccessClassifications(),
      globalDataService.sectors(),
    ]).then((response) => {
      return {
        languages: response[0],
        departments: response[1],
        divisions: response[2],
        docTypes: response[3],
        countries: response[4],
        topics: response[5],
        categories: response[6],
        accessClassifications: response[7],
        sectors: response[8],
      } as FormListData;
    });
  });

  const [otherDivisionsOptions, setOtherDivisionsOptions] = useState<string[]>(
    []
  );
  const [otherDepartmentsOptions, setOtherDepartmentsOptions] = useState<
    string[]
  >([]);

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .nullable()
      .required(t("requiredField")!)
      .max(250)
      .matches(
        /^(?!\.)(?!com[0-9]$)(?!con$)(?!lpt[0-9]$)(?!nul$)(?!prn$)[^|*?\\:<>/$"]*[^.|*?\\:<>/$"]+$/,
        t("invalidChareteres")!
      ),
    author: Yup.object().nullable().required(t("requiredField")!),
    coAuthorExternals: Yup.string().nullable().optional().max(2048),
    languages: Yup.array().min(1, t("requiredField")!),
    docType: Yup.object().shape({
      id: Yup.number().integer().moreThan(0, t("requiredField")!).required(),
    }),
    abstract: Yup.string().nullable().required(t("requiredField")!).max(2046),
    countries: Yup.array().optional(),
    topics: Yup.array().min(1, t("requiredField")!),
    //creationDate: Yup.date().required(t("requiredField")!),
    category: Yup.object().shape({
      id: Yup.number().integer().moreThan(0, t("requiredField")!).required(),
    }),
    recordAccessClassification: Yup.object().shape({
      id: Yup.number().integer().moreThan(0, t("requiredField")!).required(),
    }),
    eventOrSourceOfContent: Yup.string().nullable().max(64),
    sectors: Yup.array().optional(),
    translations: Yup.array().of(
      Yup.object().shape({
        title: Yup.string().nullable().required(t("requiredField")!).max(250),
        abstract: Yup.string()
          .nullable()
          .required(t("requiredField")!)
          .max(2046),
      })
    ),
  });

  const formik = useFormik<MetadataModel>({
    initialValues: model || _emptyGeneralInfo,
    onSubmit: () => console.log("submit"),
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const save = (values: MetadataModel, nextStep: boolean) => {
    service(
      () =>
        workflowService.metadataSave(
          ticketNumber!,
          values,
          nextStep,
          uploadCovers
        ),
      {
        callback: (response) => {
          if (!nextStep) setSaveSuccess(true);
          else {
            continueDialogShowSet(true);
          }
        },
        loader: setSaving,
      }
    );
  };

  const cancel = (formik: FormikProps<MetadataModel>) => {
    if (formik.dirty) cancelDialogShowSet(true);
    else navigate(-1);
  };

  const buildTooltip = (labelKey: string) => (
    <IconTooltip title={t(labelKey)} />
  );

  const optionalLabel = (labelKey: string) => (
    <p>
      {t(labelKey)}
      <em className={styles.optional}>{t("optionalField")}</em>
    </p>
  );

  const onDeleteCover = (index: number) => {
    if (formik.values.covers) {
      const aux = [...formik.values.covers];
      aux.splice(index, 1);

      formik.setFieldValue("covers", [...aux]);
    }
  };

  const handleSaveCondition = (): boolean => {
    if (Boolean(creationDateError)) {
      return false;
    } else {
      return true;
    }
  };

  const handleContinueCondition = (): boolean => {
    var saveCondition = handleSaveCondition();

    if (
      !saveCondition ||
      model?.finalVersionDocuments.some(
        (x) =>
          !uploadCovers.some((u) => u.language === x.language) &&
          !formik.values.covers.some((c) => c.language === x.language)
      )
    ) {
      setRequiredDocumentError(true);
      return false;
    } else {
      setRequiredDocumentError(null);
      return true;
    }
  };

  useEffect(() => {
    if (model) {
      setIsReadOnly(model.isCompleted || !model.canEdit);

      setOtherDepartmentsOptions([
        ...(formListData?.departments.filter(
          (x) => !model.leadingDepartments.includes(x)
        ) || []),
      ]);

      setOtherDivisionsOptions([
        ...(formListData?.divisions.filter(
          (x) => !model.leadingDivisions.includes(x)
        ) || []),
      ]);
    }
  }, [model, formListData]);

  return (
    <SpinnerArea loading={loading || loadingListData || saving}>
      <form
        onSubmit={formik.handleSubmit}
        onReset={formik.handleReset}
        className={styles.metadataForm}
      >
        <Grid container>
          <Grid item sm={12}>
            <div className={styles.header}>
              <p>
                <strong>{t("publicationTicketNumber")}: </strong>
                {currentWorkflow?.ticketNumber}
              </p>
              <p>
                <strong>{t("completeMetadata")}</strong>
              </p>
              <p>
                <em>{t("metadataInfo_1")}</em>
              </p>
              <p>
                <em>{t("metadataInfo_2")}</em>
              </p>
            </div>
          </Grid>
        </Grid>
        {model && (
          <div className={styles.formContainer}>
            <div className={styles["area-title"]}>
              <FormControl fullWidth>
                <FormLabel id="title-label">{t("title")}</FormLabel>
                <TextField
                  id="title"
                  name="title"
                  placeholder={t("title") || ""}
                  fullWidth
                  inputProps={{ readOnly: isReadOnly }}
                  value={formik.values.title || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={formik.touched.title && formik.errors.title}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                />
              </FormControl>
            </div>
            <div className={styles["area-author"]}>
              <FormControl fullWidth>
                <FormLabel id="author-label">
                  {t("author")}
                  {buildTooltip("authorTooltip")}
                </FormLabel>

                <PeoplePicker
                  id="author"
                  defaultValue={model?.author || null}
                  readOnly={true}
                  disabled
                  placeholder={t("author").toString()}
                  onChange={(newValue) => {
                    formik.setFieldValue("author", newValue);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.author && formik.errors.author}
                />
              </FormControl>
            </div>
            <div className={styles["area-coauthor-externals"]}>
              <FormControl fullWidth>
                <FormLabel id="externalCoAuthors-label">
                  {optionalLabel("externalCoAuthorsLabel")}
                </FormLabel>
                <TextField
                  id="coAuthorExternals"
                  name="coAuthorExternals"
                  placeholder={t("externalCoAuthorsLabel") || ""}
                  fullWidth
                  inputProps={{ readOnly: isReadOnly }}
                  value={formik.values.coAuthorExternals || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.coAuthorExternals &&
                    formik.errors.coAuthorExternals
                  }
                  error={
                    formik.touched.coAuthorExternals &&
                    Boolean(formik.errors.coAuthorExternals)
                  }
                />
              </FormControl>
            </div>
            <div className={styles["area-coauthor-internals"]}>
              <FormControl fullWidth>
                <FormLabel id="internalCoAuthors-label">
                  {optionalLabel("internalCoAuthorsLabel")}
                  {buildTooltip("internalCoAuthorsTooltip")}
                </FormLabel>
                <PeoplePickerMultiple
                  id="coAuthorInternals"
                  defaultValue={model?.coAuthorInternals || []}
                  readOnly={isReadOnly}
                  placeholder={t("internalCoAuthorsLabel").toString()}
                  onChange={(newValue) => {
                    formik.setFieldValue("coAuthorInternals", newValue);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.coAuthorInternals &&
                    formik.errors.coAuthorInternals
                  }
                />
              </FormControl>
            </div>
            <div className={styles["area-topics"]}>
              <FormControl fullWidth>
                <FormLabel id="topics-label">{t("topicsLabel")}</FormLabel>
                <SelectMultiple
                  id="topics"
                  placeholder={t("topicsLabel").toString()}
                  currentValues={formik.values.topics || []}
                  keySelector={(x) => x}
                  labelSelector={(x) => x}
                  source={formListData?.topics || []}
                  readOnly={isReadOnly}
                  onChange={(newValues) =>
                    formik.setFieldValue("topics", newValues)
                  }
                  onBlur={formik.handleBlur}
                  error={formik.touched.topics && formik.errors.topics}
                />
              </FormControl>
            </div>

            <div className={styles["area-languages"]}>
              <FormControl fullWidth>
                <FormLabel id="languages-label">{t("mainLanguages")}</FormLabel>
                <SelectMultiple
                  id="languages"
                  placeholder={t("selectLanguages").toString()}
                  currentValues={formik.values.languages || []}
                  keySelector={(x) => x}
                  labelSelector={(x) => t(`languages.${x}`)}
                  source={formListData?.languages || []}
                  readOnly={isReadOnly}
                  onChange={(newValues) =>
                    formik.setFieldValue("languages", newValues)
                  }
                  onBlur={formik.handleBlur}
                  error={formik.touched.languages && formik.errors.languages}
                />
              </FormControl>
            </div>
            <div className={styles["area-doc-type"]}>
              <FormControl fullWidth>
                <FormLabel id="docType-label">{t("docType")}</FormLabel>
                <Select
                  id="docType.id"
                  name="docType.id"
                  className={
                    getIn(formik.touched, "docType.id") &&
                    getIn(formik.errors, "docType.id")
                      ? "Mui-error"
                      : ""
                  }
                  fullWidth
                  disabled
                  inputProps={{ readOnly: true }}
                  value={formik.values.docType.id.toString()}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  displayEmpty
                  renderValue={(value) =>
                    value !== "0" ? (
                      formListData?.docTypes.find(
                        (x) => x.id === parseInt(value)
                      )?.name
                    ) : (
                      <PlaceholderControl text={t("docType")} />
                    )
                  }
                >
                  <MenuItem
                    key={`doc_types_${0}`}
                    value={0}
                    className={"selectPlaceholder"}
                  >
                    {t("docType")}
                  </MenuItem>
                  {formListData?.docTypes.map((x) => (
                    <MenuItem key={`doc_types_${x.id}`} value={x.id!}>
                      {x.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText className="Mui-error">
                  {getIn(formik.touched, "docType.id") &&
                    getIn(formik.errors, "docType.id")}
                </FormHelperText>
              </FormControl>
            </div>
            <div className={styles["area-leading-divisions"]}>
              {model?.docType.levelOfApproval === "DivisionChief" && (
                <FormControl fullWidth>
                  <FormLabel id="leadingDivisions-label">
                    {t("leadingDivisions")}{" "}
                    {buildTooltip("leadingDivisionsTooltip")}
                  </FormLabel>
                  <SelectMultiple
                    id="leadingDivisions"
                    placeholder={t("selectLeadingDivisions").toString()}
                    currentValues={formik.values.leadingDivisions || []}
                    keySelector={(x) => x}
                    labelSelector={(x) => x}
                    source={formListData?.divisions || []}
                    readOnly
                    disabled
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.leadingDivisions &&
                      formik.errors.leadingDivisions
                    }
                  />
                </FormControl>
              )}
              {model?.docType.levelOfApproval === "DepartmentChief" && (
                <FormControl fullWidth>
                  <FormLabel id="leadingDeparments-label">
                    {t("leadingDepartments")}{" "}
                    {buildTooltip("leadingDepartmentsTooltip")}
                  </FormLabel>
                  <SelectMultiple
                    id="leadingDepartments"
                    placeholder={t("selectLeadingDepartments").toString()}
                    currentValues={formik.values.leadingDepartments || []}
                    keySelector={(x) => x}
                    labelSelector={(x) => x}
                    source={formListData?.departments || []}
                    readOnly
                    disabled
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.leadingDepartments &&
                      formik.errors.leadingDepartments
                    }
                  />
                </FormControl>
              )}
            </div>
            <div className={styles["area-others-divisions"]}>
              {model?.docType.levelOfApproval === "DivisionChief" && (
                <FormControl fullWidth>
                  <FormLabel id="otherDivisionsInvolved-label">
                    {optionalLabel("otherDivisionsInvolved")}
                    {buildTooltip("otherDivisionsInvolvedTooltip")}
                  </FormLabel>
                  <SelectMultiple
                    id="otherDivisionsInvolved"
                    placeholder={t("selectOtherDivisionsInvolved").toString()}
                    currentValues={formik.values.otherDivisionsInvolved || []}
                    keySelector={(x) => x}
                    labelSelector={(x) => x}
                    source={otherDivisionsOptions}
                    readOnly
                    disabled
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  />
                </FormControl>
              )}
              {model?.docType.levelOfApproval === "DepartmentChief" && (
                <FormControl fullWidth>
                  <FormLabel id="otherDepartmentsInvolved-label">
                    {optionalLabel("otherDepartmentsInvolved")}
                    {buildTooltip("otherDepartmentsInvolvedTooltip")}
                  </FormLabel>
                  <SelectMultiple
                    id="otherDepartmentsInvolved"
                    placeholder={t("selectOtherDepartmentsInvolved").toString()}
                    currentValues={formik.values.otherDepartmentsInvolved || []}
                    keySelector={(x) => x}
                    labelSelector={(x) => x}
                    source={otherDepartmentsOptions}
                    readOnly
                    disabled
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </FormControl>
              )}
            </div>

            <div className={styles["area-creation-date"]}>
              <FormControl fullWidth>
                <FormLabel id="creationDate-label">
                  {t("creationDateLabel")}
                </FormLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    className={styles.picker}
                    disabled={isReadOnly}
                    format="MM/DD/YYYY"
                    minDate={dayjs().set("year", 1990)}
                    defaultValue={
                      model.creationDate
                        ? dayjs(model.creationDate, "YYYY-MM-DD")
                        : null
                    }
                    onChange={(newValue: Dayjs | null) => {
                      const isValidDate = newValue?.isValid();
                      var entryDate = isValidDate ? newValue?.toDate() : null;
                      formik.setFieldValue("creationDate", entryDate);

                      if (!isValidDate) {
                        setCreationDateError(t("requiredField"));
                      } else {
                        setCreationDateError(null);
                      }
                    }}
                    onError={(newError) => {
                      formik.setFieldValue("creationDate", null);
                    }}
                    readOnly={isReadOnly}
                    slotProps={{
                      textField: {
                        name: "creationDate",
                        id: "creationDate",
                        fullWidth: true,
                        onBlur: formik.handleBlur,
                        helperText: creationDateError,
                        error: Boolean(creationDateError),
                      },
                    }}
                  />
                </LocalizationProvider>
              </FormControl>
            </div>

            <div className={styles["area-category"]}>
              <FormControl fullWidth>
                <FormLabel id="category-label">{t("categoryInWebsite")}</FormLabel>
                <Select
                  id="category.id"
                  name="category.id"
                  className={
                    getIn(formik.touched, "category.id") &&
                    getIn(formik.errors, "category.id")
                      ? "Mui-error"
                      : ""
                  }
                  fullWidth
                  inputProps={{ readOnly: isReadOnly }}
                  value={formik.values.category?.id.toString()}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  displayEmpty
                  renderValue={(value) =>
                    value !== "0" ? (
                      formListData?.categories.find(
                        (x) => x.id === parseInt(value)
                      )?.name
                    ) : (
                      <PlaceholderControl text={t("categoryInWebsite")} />
                    )
                  }
                >
                  <MenuItem
                    key={`category_${0}`}
                    value={0}
                    className={"selectPlaceholder"}
                  >
                    {t("categoryInWebsite")}
                  </MenuItem>
                  {formListData?.categories.map((x) => (
                    <MenuItem key={`category_${x.id}`} value={x.id!}>
                      {x.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText className="Mui-error">
                  {getIn(formik.touched, "category.id") &&
                    getIn(formik.errors, "category.id")}
                </FormHelperText>
              </FormControl>
            </div>

            <div className={styles["area-countries"]}>
              <FormControl fullWidth>
                <FormLabel id="countries-label">
                  {optionalLabel("countriesLabel")}
                  {buildTooltip("countriesTooltip")}
                </FormLabel>
                <SelectMultiple
                  id="countries"
                  placeholder={t("selectCountries").toString()}
                  currentValues={formik.values.countries || []}
                  keySelector={(x) => x}
                  labelSelector={(x) => x}
                  source={formListData?.countries || []}
                  readOnly={isReadOnly}
                  onChange={(newValues) =>
                    formik.setFieldValue("countries", newValues)
                  }
                  onBlur={formik.handleBlur}
                  error={formik.touched.countries && formik.errors.countries}
                />
              </FormControl>
            </div>

            <div className={styles["area-record-access-classification"]}>
              <FormControl fullWidth>
                <FormLabel id="recordAccessClassification-label">
                  {t("recordAccessClassification")}
                </FormLabel>
                <Select
                  id="recordAccessClassification.id"
                  name="recordAccessClassification.id"
                  className={
                    getIn(formik.touched, "recordAccessClassification.id") &&
                    getIn(formik.errors, "recordAccessClassification.id")
                      ? "Mui-error"
                      : ""
                  }
                  fullWidth
                  disabled
                  inputProps={{ readOnly: isReadOnly }}
                  value={formik.values.recordAccessClassification.id.toString()}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  displayEmpty
                  renderValue={(value) =>
                    value !== "0" ? (
                      formListData?.accessClassifications.find(
                        (x) => x.id === parseInt(value)
                      )?.name
                    ) : (
                      <PlaceholderControl
                        text={t("recordAccessClassification")}
                      />
                    )
                  }
                >
                  <MenuItem
                    key={`recordAccessClassification_${0}`}
                    value={0}
                    className={"selectPlaceholder"}
                  >
                    {t("recordAccessClassification")}
                  </MenuItem>
                  {formListData?.accessClassifications.map((x) => (
                    <MenuItem
                      key={`recordAccessClassification_${x.id}`}
                      value={x.id!}
                    >
                      {x.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText className="Mui-error">
                  {getIn(formik.touched, "recordAccessClassification.id") &&
                    getIn(formik.errors, "recordAccessClassification.id")}
                </FormHelperText>
              </FormControl>
            </div>

            <div className={styles["area-event-or-source"]}>
              <FormControl fullWidth>
                <FormLabel id="eventOrSourceOfContent-label">
                  {optionalLabel("eventOrSourceOfContent")}
                </FormLabel>
                <TextField
                  id="eventOrSourceOfContent"
                  name="eventOrSourceOfContent"
                  placeholder={t("eventOrSourceOfContent") || ""}
                  fullWidth
                  inputProps={{ readOnly: isReadOnly }}
                  value={formik.values.eventOrSourceOfContent || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.eventOrSourceOfContent &&
                    formik.errors.eventOrSourceOfContent
                  }
                  error={
                    formik.touched.eventOrSourceOfContent &&
                    Boolean(formik.errors.eventOrSourceOfContent)
                  }
                />
              </FormControl>
            </div>

            <div className={styles["area-sector"]}>
              <FormControl fullWidth>
                <FormLabel id="sector-label">
                  {optionalLabel("sector")}
                </FormLabel>
                <SelectMultiple
                  id="sector"
                  placeholder={t("sector")!}
                  currentValues={formik.values.sectors || []}
                  keySelector={(x) => x}
                  labelSelector={(x) => x}
                  source={formListData?.sectors || []}
                  readOnly={isReadOnly}
                  onChange={(newValues) =>
                    formik.setFieldValue("sectors", newValues)
                  }
                  onBlur={formik.handleBlur}
                  error={formik.touched.sectors && formik.errors.sectors}
                />
              </FormControl>
            </div>

            <div className={styles["area-abstract"]}>
              <FormControl fullWidth>
                <FormLabel id="description-label">
                  <p>
                    {t("generalInfoDescription")}
                    <em>{t("generalInfoDescriptionHelpText")}</em>
                  </p>
                </FormLabel>
                <TextField
                  id="abstract"
                  name="abstract"
                  placeholder={t("description") || ""}
                  fullWidth
                  rows={3}
                  multiline
                  inputProps={{ readOnly: isReadOnly }}
                  value={formik.values.abstract || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={formik.touched.abstract && formik.errors.abstract}
                  error={
                    formik.touched.abstract && Boolean(formik.errors.abstract)
                  }
                />
              </FormControl>
            </div>

            <Divider className={styles["area-divider"]} />

            <div className={styles["area-publications-site"]}>
              <p>
                <strong>{t("informationForIDBInvestPublicationsSite")}</strong>
                {buildTooltip("informationForIDBInvestPublicationsSiteTooltip")}
              </p>
            </div>

            {formik.values.translations.map((translation, index) => (
              <MetadataTranslation
                key={`title_translation_${translation.code}`}
                index={index}
                translation={translation}
                formik={formik}
                propertyName="title"
                isReadOnly={isReadOnly}
              />
            ))}

            {formik.values.translations.map((translation, index) => (
              <MetadataTranslation
                key={`abstract_translation_${translation.code}`}
                index={index}
                translation={translation}
                formik={formik}
                propertyName="abstract"
                label="description"
                isReadOnly={isReadOnly}
              />
            ))}

            <div className={styles["area-front-page"]}>
              <FormControl fullWidth>
                <FormLabel id="selectedFinalDocument-label">
                  {t("selectDocumentToAssignAFrontPage")}
                </FormLabel>
                <Select
                  id="selectedFinalDocument"
                  name="selectedFinalDocument"
                  fullWidth
                  inputProps={{ readOnly: isReadOnly }}
                  value={selectedFinalDocument || ""}
                  onChange={(event: SelectChangeEvent) => {
                    //formik.setFieldValue("selectedFinalDocument", event.target.value);
                    setSelectedFinalDocument(event.target.value);
                  }}
                  //onBlur={formik.handleBlur}
                  displayEmpty
                  renderValue={(value) =>
                    value !== "" ? (
                      model?.finalVersionDocuments.find(
                        (x) => x.language === value
                      )?.name
                    ) : (
                      <PlaceholderControl text={t("selectDocument")} />
                    )
                  }
                >
                  <MenuItem
                    key={`languages_${0}`}
                    value={0}
                    className={"selectPlaceholder"}
                  >
                    {t("selectDocument")}
                  </MenuItem>
                  {model?.finalVersionDocuments?.map((x) => (
                    <MenuItem key={`languages_${x.id}`} value={x.language}>
                      {x.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FileUploader
                customClassName={styles["upload-covers"]}
                onChange={(file: File | File[] | FileWrapper[]) => {
                  setUploadCovers(file as FileWrapper[]);
                  //formik.setFieldValue("selectedFinalDocument", null);
                  setSelectedFinalDocument(undefined);
                  formik.validateForm();
                }}
                allowedExtensions={[".jpg", ".jpeg", ".png"]}
                isReadOnly={isReadOnly || !selectedFinalDocument}
                title={t("uploadFrontPage")!}
                appendMode
                prefix={selectedFinalDocument}
                errorValidate={requiredDocumentError}
              />
            </div>

            <div className={styles["area-covers"]}>
              {formik.values.covers && formik.values.covers.length > 0 && (
                <Covers
                  documents={formik.values.covers}
                  onDelete={onDeleteCover}
                />
              )}
            </div>
          </div>
        )}
        <div className={styles.formActions}>
          <Button className={styles.cancel} onClick={() => cancel(formik)}>
            {t("cancel")}
          </Button>
          <Button
            className={styles.save}
            disabled={isReadOnly}
            onClick={() =>
              validateBeforeExecute(
                formik,
                () => save(formik.values, false),
                handleSaveCondition
              )
            }
          >
            {t("save")}
          </Button>
          <Button
            className={styles.continue}
            disabled={isReadOnly}
            onClick={() =>
              validateBeforeExecute(
                formik,
                () => save(formik.values, true),
                handleContinueCondition
              )
            }
          >
            {t("continue")}
          </Button>
        </div>
      </form>
      <SaveSuccessDialog open={saveSuccess} />
      <CancelDialog
        open={cancelDialogShow}
        yesAction={() => navigate(-1)}
        noAction={() => cancelDialogShowSet(false)}
      />
      <ContinueDialog
        open={continueDialogShow}
        setOpen={continueDialogShowSet}
        action={() => {}}
      />
    </SpinnerArea>
  );
};

export default Metadata;
